import { Platform, type StyleProp, View, type ViewStyle } from 'react-native';
import { colors } from '../../theme/colors';

// Superhuman/Raycast glass surfaces — the only place raw hsla tuples are allowed,
// since LinearGradient needs explicit stops the token scale doesn't express.
export const GLASS_GRADIENT = ['hsla(175, 40%, 14%, 0.5)', 'hsla(208, 42%, 10%, 0.5)'] as const;
export const GLASS_GRADIENT_STRONG = [
  'hsla(175, 55%, 16%, 0.6)',
  'hsla(208, 42%, 10%, 0.6)',
] as const;
export const GLASS_GRADIENT_SUBTLE = [
  'hsla(175, 35%, 13%, 0.4)',
  'hsla(208, 42%, 9%, 0.5)',
] as const;
export const WARNING_GRADIENT = ['hsla(43, 89%, 50%, 0.24)', 'hsla(43, 89%, 50%, 0.04)'] as const;

export const GLASS_GRADIENT_START = { x: 0, y: 0 } as const;
export const GLASS_GRADIENT_END = { x: 1, y: 1 } as const;

// Soft teal bloom behind hero / pricing / CTA cards. Web uses a radial-gradient
// that fades to transparent; native falls back to a low-opacity tinted disc so
// it never crashes and still reads as a glow.
interface GlowBloomProps {
  color?: string;
  size?: number;
  style?: StyleProp<ViewStyle>;
}

export const GlowBloom = ({ color = colors.glow.cyan, size = 320, style }: GlowBloomProps) => (
  <View
    pointerEvents="none"
    style={[
      {
        position: 'absolute',
        width: size,
        height: size,
        borderRadius: size / 2,
      },
      Platform.select({
        // A soft filled teal bloom that fades to transparent — no hard ring and
        // no transparent (dark) center, unlike an outset box-shadow.
        web: {
          backgroundImage: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
        } as unknown as ViewStyle,
        default: { backgroundColor: color, opacity: 0.3 },
      }),
      style,
    ]}
  />
);

export const softHalo = Platform.select({
  web: { boxShadow: `0 0 24px ${colors.glow.cyan}` } as unknown as ViewStyle,
  default: {} as ViewStyle,
});

// Raycast-style engineering grid + top teal bloom, pinned behind the page so it
// reads as a fixed backdrop while content scrolls over it. Web-only (the grid is
// CSS background layers); native keeps the flat near-black canvas.
export const GridBackdrop = ({ style }: { style?: StyleProp<ViewStyle> }) => (
  <View
    pointerEvents="none"
    style={[
      { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 },
      Platform.select({
        web: {
          backgroundImage: [
            'radial-gradient(900px 520px at 50% -8%, hsla(175, 100%, 40%, 0.16), transparent 70%)',
            'linear-gradient(to right, hsla(180, 30%, 60%, 0.05) 1px, transparent 1px)',
            'linear-gradient(to bottom, hsla(180, 30%, 60%, 0.05) 1px, transparent 1px)',
          ].join(', '),
          backgroundSize: '100% 100%, 56px 56px, 56px 56px',
        } as unknown as ViewStyle,
        default: {},
      }),
      style,
    ]}
  />
);
